import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { PerStageCounts, Stage } from "@attendik/types/schemas";

import { api } from "../api/client.js";
import { useAuth } from "../store/auth.js";

// CR-04. Picker screen shown to operators who are assigned to 2+ stages on
// the same event. Each card displays the stage name and `currently_in /
// capacity` so the operator can pick the door they're standing at.
//
// Tapping a card navigates to `/scan/:eventId?stage=<stage_id>`. The
// stage_id rides on the search param so a reload preserves the selection.
export function StagePickerPage(): JSX.Element {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { operator, logout } = useAuth();

  const [stages, setStages] = useState<Stage[] | null>(null);
  const [counts, setCounts] = useState<Record<string, PerStageCounts>>({});
  const [eventName, setEventName] = useState<string | null>(null);

  // Load the operator's assigned stages on mount.
  useEffect(() => {
    if (!eventId) {
      return;
    }
    let cancelled = false;
    api
      .getStages(eventId, true)
      .then(async (list) => {
        if (cancelled) return;
        // Edge cases — keep the picker honest if the assignment changes
        // while the operator was on this screen.
        if (list.length === 0) {
          navigate(`/scan/${eventId}`, { replace: true });
          return;
        }
        if (list.length === 1) {
          navigate(`/scan/${eventId}?stage=${list[0]!.id}`, { replace: true });
          return;
        }
        setStages(list);

        // Load per-stage occupancy in parallel so each card can show
        // currently_in / capacity. Failures here are silent — the cards
        // gracefully render without numbers.
        const entries = await Promise.all(
          list.map(async (stage) => {
            try {
              const c = await api.getStageCounts(stage.id);
              return [stage.id, c] as const;
            } catch {
              return null;
            }
          })
        );
        if (cancelled) return;
        const map: Record<string, PerStageCounts> = {};
        for (const entry of entries) {
          if (entry) {
            map[entry[0]] = entry[1];
          }
        }
        setCounts(map);
      })
      .catch(() => {
        // On hard failure, fall through to the scan page; it will surface
        // its own offline / empty state.
        if (!cancelled) {
          navigate(`/scan/${eventId}`, { replace: true });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [eventId, navigate]);

  // Look up the event name for the top bar.
  useEffect(() => {
    if (!eventId) {
      return;
    }
    let cancelled = false;
    api
      .getEvents()
      .then((events) => {
        const match = events.find((e) => e.id === eventId);
        if (!cancelled && match) {
          setEventName(match.name);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const handleLogout = useCallback(async (): Promise<void> => {
    try {
      await api.logout();
    } catch {
      // Local logout is the source of truth — server-side is best-effort.
    }
    logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  return (
    <div className="flex h-[100dvh] w-full flex-col overflow-hidden overscroll-none bg-slate-950 text-white">
      <header className="flex flex-col bg-slate-900 px-4 py-2">
        <div className="truncate text-base font-semibold">
          {eventName ?? "Event"}
        </div>
        <div className="mt-1 flex items-center justify-between">
          <span className="truncate text-sm text-slate-300">
            Operator: {operator?.name ?? ""}
          </span>
          <button
            type="button"
            onClick={() => void handleLogout()}
            aria-label="Log out"
            className="ml-3 shrink-0 rounded bg-slate-700 px-3 py-1 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            Logout
          </button>
        </div>
      </header>

      <main
        className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-6"
        data-testid="stage-picker"
      >
        <h1 className="text-center text-lg font-semibold text-slate-200">
          Pick your stage
        </h1>
        {stages === null ? (
          <p className="text-center text-slate-400">Loading…</p>
        ) : (
          stages.map((stage) => {
            const c = counts[stage.id];
            const denom =
              stage.capacity !== null && stage.capacity !== undefined
                ? ` / ${stage.capacity}`
                : "";
            const occupancy = c ? `${c.currently_in}${denom}` : `—${denom}`;
            return (
              <button
                key={stage.id}
                type="button"
                onClick={() =>
                  navigate(`/scan/${eventId}?stage=${stage.id}`)
                }
                className="flex w-full items-start justify-between gap-4 rounded-lg bg-slate-800 px-5 py-6 text-left transition-colors hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400"
              >
                <span className="min-w-0 break-words text-xl font-semibold leading-snug">
                  {stage.name}
                </span>
                <span className="shrink-0 pt-1 text-base tabular-nums text-slate-300">
                  {occupancy}
                </span>
              </button>
            );
          })
        )}
      </main>
    </div>
  );
}
